import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import {
  Center,
  Loader,
  Paper,
  Stack,
  Text,
  Button,
  Alert,
  Container
} from '@mantine/core'
import { IconLock, IconAlertCircle } from '@tabler/icons-react'
import { checkAuthStatus } from '../../store/slices/authSlice'
import AuthButton from './AuthButton'

function ProtectedRoute({ 
  children, 
  fallback = null, 
  requireAuth = true,
  redirectToAuth = true,
  redirectTo = null,
  minRole = null,
  showLoader = true 
}) {
  const dispatch = useDispatch()
  const location = useLocation()
  const { 
    isAuthenticated, 
    user, 
    authStatus, 
    loading, 
    error 
  } = useSelector((state) => state.auth)

  useEffect(() => {
    // Check auth status if we haven't done so yet
    if (authStatus === 'idle') {
      dispatch(checkAuthStatus())
    }
  }, [dispatch, authStatus])

  // Show loader while checking authentication
  if (authStatus === 'checking' || (authStatus === 'idle' && showLoader)) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Checking authentication...
          </Text>
        </Stack>
      </Center>
    )
  }

  // Show error if auth check failed
  if (error && authStatus !== 'authenticated') {
    return (
      <Container size="sm" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Authentication Error"
          color="red"
          variant="light"
        >
          <Stack gap="sm">
            <Text size="sm">
              {error}
            </Text>
            <Button
              size="sm"
              onClick={() => dispatch(checkAuthStatus())}
              loading={loading}
            >
              Retry
            </Button>
          </Stack>
        </Alert>
      </Container>
    )
  }

  // Check if authentication is required
  if (requireAuth && !isAuthenticated) {
    // If auth status is still checking, don't redirect yet
    if (authStatus === 'checking' || authStatus === 'idle') {
      return (
        <Center h="100vh">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">
              Checking authentication...
            </Text>
          </Stack>
        </Center>
      )
    }
    
    if (fallback) {
      return fallback
    }

    // If redirectTo is provided, redirect to that path
    if (redirectTo) {
      return <Navigate to={redirectTo} state={{ from: location }} replace />
    }

    if (redirectToAuth) {
      return (
        <Container size="sm" py="xl">
          <Paper shadow="md" p="xl" radius="md" withBorder>
            <Stack align="center" gap="lg">
              <IconLock size={64} color="var(--mantine-color-blue-6)" />
              <Text size="xl" fw={600} ta="center">
                Authentication Required
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                You need to sign in to access this content.
              </Text>
              <AuthButton variant="filled" size="lg" />
            </Stack>
          </Paper>
        </Container>
      )
    }

    return null
  }

  // Check role-based access if minRole is specified
  if (requireAuth && isAuthenticated && minRole && user) {
    const userRoles = user.roles || []
    const hasRequiredRole = userRoles.includes(minRole) || user.is_staff || user.is_superuser

    if (!hasRequiredRole) {
      return (
        <Container size="sm" py="xl">
          <Paper shadow="md" p="xl" radius="md" withBorder>
            <Stack align="center" gap="lg">
              <IconLock size={64} color="var(--mantine-color-red-6)" />
              <Text size="xl" fw={600} ta="center" c="red">
                Access Denied
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                You don't have the required permissions to access this content.
                Required role: {minRole}
              </Text>
              <Button variant="light" onClick={() => window.history.back()}>
                Go Back
              </Button>
            </Stack>
          </Paper>
        </Container>
      )
    }
  }

  // User is authenticated and authorized, render children
  return children
}

export default ProtectedRoute